// DRV-5b(ii) (#2883) — the artefact filing badge: API shape and state → copy.
//
// GET /artefacts/:id/storage-status (apps/api/src/routes/artefactStorage.routes.ts).
// The copy is the 5b(ii) recon's item-4 table (#2883 comment 6047593405),
// rows 1–21, as ruled by D-1..D-7 (comment 6047987042). `row` on the result
// is that table's row number, so a test (and a reader) can tie a rendering
// back to the table.
//
// Rules this file owns:
// - No raw `lastError` ever reaches copy. Every code maps to wording here;
//   an unknown code falls back by status (row 21).
// - "Queued (after delegate window)" is `notBefore > now`, never the row's
//   trigger: a File now press inside the window makes a `manual` row.
// - Actions (File now / Retry) are TENANT_ADMIN only (D-1), and only while
//   Drive is connected: a legacy authorized row with no account reads as
//   not connected, and a press there just re-blocks. The server enforces the
//   role itself; this only decides what to show.
// - A stale file shows the out-of-date notice on any status. When the server
//   has said there is no approved version (a 409 `no_approved_version`, the
//   only signal the web has; #3251), the notice is the revoked-version text
//   instead and no action is offered.

import { safeDriveUrl } from '../../lib/driveUrl';

export type FilingStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'BLOCKED' | 'SKIPPED';

export interface FilingRow {
  status:    string;
  kind:      string;
  trigger:   string;
  versionId: string;
  version:   string;
  notBefore: string;
  attempts:  number;
  lastError: string | null;
  updatedAt: string;
}

export interface FiledDocument {
  format:       string;
  name:         string;
  status:       string;
  webUrl:       string | null;
  filedVersion: string | null;
  filedAt:      string | null;
  stale:        boolean;
}

export interface StorageStatus {
  artefactId: string;
  connected:  boolean;
  stale:      boolean;
  filing:     FilingRow | null;
  describe:   FilingRow | null;
  files:      FiledDocument[];
}

/** A body we can render from; anything else renders no badge. */
export function isStorageStatus(body: unknown): body is StorageStatus {
  const b = body as Partial<StorageStatus> | null;
  return !!b && typeof b === 'object'
    && typeof b.connected === 'boolean'
    && typeof b.stale === 'boolean'
    && (b.filing === null || (typeof b.filing === 'object' && typeof b.filing?.status === 'string'))
    && Array.isArray(b.files);
}

export const SETTINGS_DRIVE_PATH = '/settings/integrations/gdrive';
export const SETTINGS_STORAGE_PATH = '/settings/storage';

export const STALE_NOTICE = 'Drive is out of date';
export const REVOKED_VERSION_NOTICE = 'Drive holds a revoked version. It will be replaced at the next approval.';

export type FilingAction = 'file_now' | 'retry' | 'recreate';
export type FilingTone = 'neutral' | 'progress' | 'ok' | 'warn' | 'error';

export interface FilingView {
  /** The recon table's row number (item 4). */
  row:          number;
  label:        string;
  tone:         FilingTone;
  /** Row 4: when the delegate window ends (ISO). */
  until:        string | null;
  /** Row 9: one link per filed format, each through safeDriveUrl. */
  links:        { format: string; href: string }[];
  /** Row 9: the newest filedAt of the filed documents (ISO). */
  filedAt:      string | null;
  /** Admin only, connected only. */
  action:       FilingAction | null;
  /** Admin only: where to fix it. */
  settingsLink: 'drive' | 'folders' | null;
  /** Stale notice: 'notice' (File now) or 'revoked' (#3251, no action). */
  stale:        'notice' | 'revoked' | null;
  /** A press found no approved version, but the file isn't stale: show the neutral line (R1-4). */
  neutralNote:  boolean;
}

export interface FilingContext {
  isAdmin: boolean;
  now:     number;
  /** The artefact's latest version is approved/handed_off (row 1's signal when no filing row exists). */
  latestApproved: boolean;
  /** A File now press returned 409 `reason: 'no_approved_version'`. */
  noApprovedVersion: boolean;
}

// Footnote 1: codes a PENDING row carries while it waits to be tried again.
const WAITING_CODES = new Set([
  'delegate_window', 'folder_in_progress', 'fence_refused', 'parent_missing', 'convert_failed',
  'drive_rate_limited', 'drive_unavailable', 'timeout', 'network', 'token_unavailable',
  'supplier_error', 'unexpected',
]);
const isWaiting = (code: string) => WAITING_CODES.has(code) || code.startsWith('db_');

// Footnote 2: FAILED codes with their own wording; every other FAILED code
// (lease_expired, drive_*, provider codes, db_*, unexpected, unknown) is row
// 21's "Drive filing failed". All of them get Retry.
const FAILED_COPY = new Map<string, string>(Object.entries({
  revision_cap:         "Drive's revision limit reached",
  name_exhausted:       "couldn't find a free file name",
  drive_quota_exceeded: 'Drive storage is full',
  drive_forbidden:      'Drive refused access',
}));
const FAILED_FALLBACK = 'Drive filing failed';

type Base = Pick<FilingView, 'row' | 'label' | 'tone'> & Partial<Pick<FilingView, 'until' | 'links' | 'filedAt' | 'settingsLink'>> & {
  /** The action the row offers an admin, before the role and connection checks. */
  offers: FilingAction | null;
};

function baseView(s: StorageStatus, ctx: FilingContext): Base | null {
  const f = s.filing;
  if (!f) {
    // Row 1: nothing filed and no approved version to file. After a 409
    // no_approved_version the badge stays (R1-4; describeFiling drops the action).
    if (!ctx.latestApproved) return null;
    if (!s.connected) return { row: 2, label: 'Not connected', tone: 'neutral', settingsLink: 'drive', offers: null };
    return { row: 3, label: 'Not filed', tone: 'neutral', offers: 'file_now' };
  }
  const code = f.lastError;
  switch (f.status) {
    case 'PENDING': {
      const nb = Date.parse(f.notBefore);
      if (!Number.isNaN(nb) && nb > ctx.now) {
        return { row: 4, label: 'Queued (after delegate window)', tone: 'progress', until: f.notBefore, offers: null };
      }
      if (code === null) return { row: 5, label: 'Queued', tone: 'progress', offers: null };
      if (code === 'account_changed') return { row: 7, label: 'Queued — Google account changed', tone: 'progress', offers: null };
      return { row: isWaiting(code) ? 6 : 21, label: 'Queued — retrying', tone: 'progress', offers: null };
    }
    case 'RUNNING':
      return { row: 8, label: 'Filing…', tone: 'progress', offers: null };
    case 'DONE': {
      const links = s.files.flatMap((d) => {
        const href = safeDriveUrl(d.webUrl);
        return href ? [{ format: d.format, href }] : [];
      });
      const filedAt = s.files.map((d) => d.filedAt).filter((t): t is string => !!t && !Number.isNaN(Date.parse(t)))
        .sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
      return { row: 9, label: `Filed v${f.version}`, tone: 'ok', links, filedAt, offers: 'file_now' };
    }
    case 'FAILED': {
      if (code === 'drive_file_missing') return { row: 12, label: 'Failed — the Drive file was deleted', tone: 'error', offers: 'recreate' };
      if (code === 'file_too_large') return { row: 13, label: 'Failed — document is over 5 MB', tone: 'error', offers: 'file_now' };
      if (code === 'render_unsupported') return { row: 14, label: "Failed — this content can't be filed yet", tone: 'error', offers: 'file_now' };
      const copy = code === null ? undefined : FAILED_COPY.get(code);
      if (copy) return { row: 11, label: `Failed — ${copy}`, tone: 'error', offers: 'retry' };
      return { row: 21, label: `Failed — ${FAILED_FALLBACK}`, tone: 'error', offers: 'retry' };
    }
    case 'BLOCKED':
      if (code === 'not_connected') return { row: 15, label: 'Blocked — reconnect Google Drive', tone: 'warn', settingsLink: 'drive', offers: null };
      if (code === 'folder_failed') return { row: 16, label: 'Blocked — a Drive folder needs attention', tone: 'warn', settingsLink: 'folders', offers: null };
      if (code === 'account_shared' || code === 'folders_held') {
        return { row: 17, label: 'Blocked — these folders belong to another workspace', tone: 'warn', offers: null };
      }
      return { row: 21, label: 'Blocked', tone: 'warn', offers: null };
    case 'SKIPPED':
      if (code === 'approval_revoked') return { row: 18, label: 'Delegate approval revoked — not filed', tone: 'neutral', offers: null };
      if (code === 'superseded') return { row: 19, label: 'Not filed — a newer version is filed', tone: 'neutral', offers: null };
      if (code === 'version_gone') return { row: 20, label: 'Not filed — version removed', tone: 'neutral', offers: null };
      return { row: 21, label: 'Not filed', tone: 'neutral', offers: null };
    default:
      // A status outside the CHECK set: render nothing rather than guess.
      return null;
  }
}

/** The badge for one storage-status read, or null for no badge. */
export function describeFiling(s: StorageStatus, ctx: FilingContext): FilingView | null {
  const base = baseView(s, ctx);
  if (!base) return null;
  const inFlight = s.filing?.status === 'PENDING' || s.filing?.status === 'RUNNING';

  let stale: FilingView['stale'] = null;
  let offers = base.offers;
  if (s.stale) {
    if (ctx.noApprovedVersion) {
      stale = 'revoked';
    } else {
      stale = 'notice';
      // Row 10: File now, unless the row is already queued or filing (a press
      // would 409) or the row offers Retry (the same request).
      if (!inFlight && offers === null) offers = 'file_now';
    }
  }
  // #3251: with no approved version a press can only 409.
  if (ctx.noApprovedVersion) offers = null;
  // R1-4 (D-4): no approved version and NOT stale. The #3251 text would be
  // false here (Drive doesn't hold a revoked version), so the badge keeps the
  // refreshed status and shows the neutral line, CONFLICT_LINE, instead.
  const neutralNote = ctx.noApprovedVersion && !s.stale;

  // D-1: TENANT_ADMIN only. Connected only (rows 2 and 15; legacy no-account).
  const action = ctx.isAdmin && s.connected ? offers : null;
  const settingsLink = ctx.isAdmin ? base.settingsLink ?? null : null;

  return {
    row: base.row, label: base.label, tone: base.tone,
    until: base.until ?? null, links: base.links ?? [], filedAt: base.filedAt ?? null,
    action, settingsLink, stale, neutralNote,
  };
}
